import { Link } from "react-router-dom";
import { Badge, Banner, Spinner, Table, type Column } from "../../components";
import { useBatchRuns } from "../../api/hooks";
import { formatDate, type BatchRunSummary } from "../../api/types";
import { t } from "../../i18n/t";

/** PracticeOverview.html's batch-run status pills: the API's SHOUTED snake statuses as sentence-case
 *  labels with the state tone. */
const BATCH_STATUS: Readonly<
  Record<string, readonly [string, "run" | "warn" | "pass" | "fail" | "neutral"]>
> = {
  DRAFT: ["Draft", "neutral"],
  RUNNING: ["Running", "run"],
  AWAITING_APPROVALS: ["Awaiting approvals", "warn"],
  COMPLETE: ["Complete", "pass"],
  COMPLETED: ["Complete", "pass"],
  FAILED: ["Failed", "fail"],
  CANCELLED: ["Cancelled", "neutral"],
};

function batchStatusLabel(status: string): string {
  return BATCH_STATUS[status]?.[0] ?? status;
}

function batchStatusTone(status: string): "run" | "warn" | "pass" | "fail" | "neutral" {
  return BATCH_STATUS[status]?.[1] ?? "neutral";
}

/**
 * Fable #14 fix: this used to read a browser-local id list out of `sessionStorage`
 * (`screens/practice/batchRunSession.ts`) because `GET /practices/{practice_id}/batch-runs` did not exist
 * yet. It landed (P5-backend-2), so this tab now reads the practice's durable, server-side history through
 * `useBatchRuns`, visible from any device or tab a member of this practice signs into -- not just the one
 * that happened to create the run.
 */
export function BatchRunsTab({
  practiceId,
  bare = false,
}: {
  practiceId: string;
  bare?: boolean;
}): JSX.Element {
  const batchRuns = useBatchRuns(practiceId);
  const rows = batchRuns.data?.items ?? [];

  const columns: readonly Column<BatchRunSummary>[] = [
    {
      key: "id",
      header: t("practice.batch_runs.col_id"),
      render: (row) => (
        <Link
          to={`/practice/${practiceId}/batch-runs/${row.id}`}
          aria-label={t("practice.batch_runs.open", { id: row.id })}
        >
          {row.id}
        </Link>
      ),
    },
    { key: "kind", header: t("practice.batch_runs.col_kind"), render: (row) => row.kind },
    {
      key: "status",
      header: t("practice.batch_runs.col_status"),
      // PracticeOverview.html's status pills; unknown statuses fall back to the raw value.
      render: (row) => (
        <Badge tone={batchStatusTone(row.status)} dot>
          {batchStatusLabel(row.status)}
        </Badge>
      ),
    },
    {
      key: "created",
      header: t("practice.batch_runs.col_created"),
      // PracticeOverview.html shows the date, not the time.
      render: (row) => formatDate(row.created_at),
    },
    {
      key: "next_action",
      header: t("practice.batch_runs.col_next_action"),
      render: (row) =>
        row.next_action === null || row.next_action === undefined ? (
          <span className="muted">—</span>
        ) : (
          row.next_action
        ),
    },
  ];

  if (bare) {
    return batchRuns.isPending ? (
      <Spinner />
    ) : (
      <Table
        bare
        caption={t("practice.batch_runs.caption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        emptyMessage={t("practice.batch_runs.empty")}
      />
    );
  }

  return (
    <section className="stack">
      <Banner tone="info">{t("practice.overview.batch_runs_note")}</Banner>
      {batchRuns.isPending ? (
        <Spinner />
      ) : (
        <Table
          caption={t("practice.batch_runs.caption")}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          emptyMessage={t("practice.batch_runs.empty")}
        />
      )}
    </section>
  );
}
